import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import messageStyle from './chatMessages.module.css'
import { TypeMessageIcon, MoreIcons, OnlineIcon, SentIcon, PlusIcon, CrossIcon } from '../../svg';
import { CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
    addSupportClientMessage,
    getAllSupportChats,
    setSupportActiveUser,
    updateSupportReadStatus,
    createSupportMessage,
    getSupportUsersChat
} from '../redux/supportChat';
import { formatDateToHHMM, getRelativeTime } from '../helper/converter';
import { useWebSocket } from '../../context/socketContext';

const StudentSupportMessage = forwardRef((props, ref) => {
    const dispatch = useDispatch()
    const { socket, onlineUserIds } = useWebSocket();
    const { user } = useSelector(state => state.user)
    const supportHistory = useSelector(state => state.supportChat)
    const activeUser = supportHistory?.activeUser

    const [content, setContent] = useState("")
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [hasNewMessages, setHasNewMessages] = useState(false);
    const [typing, setTyping] = useState(false);
    const [chatStarted, setChatStarted] = useState(false);
    const [assignedAdminId, setAssignedAdminId] = useState(null);
    const [showChatButton, setShowChatButton] = useState(true);

    const flatListRef = useRef();
    const typingTimeoutRef = useRef(null);
    const lastRecipientRef = useRef(null);

    // Expose the openChat function via ref
    useImperativeHandle(ref, () => ({
        openChat: () => {
            console.log("🟢 Chat opened programmatically");
            setIsChatOpen(true);
            setHasNewMessages(false);
            
            // If chat hasn't started yet, automatically start it
            if (!chatStarted) {
                startSupportChat();
            }
            
            // Scroll to bottom when opened
            setTimeout(() => {
                scrollToTop();
            }, 100);
        },
        // Optionally expose other methods if needed
        closeChat: () => {
            closeChat();
        },
        isChatOpen: isChatOpen
    }));

    const toggleChat = () => {
        setIsChatOpen(!isChatOpen);
        if (!isChatOpen && !chatStarted) {
            setShowChatButton(true);
        }
        if (!isChatOpen) {
            setHasNewMessages(false);
        }
    };

    const closeChat = () => {
        stopTypingIndicator();
        setIsChatOpen(false);
        // Reset to initial state when closing chat
        setShowChatButton(true);
        setChatStarted(false);
    };

    const startSupportChat = () => {
        const supportUser = {
            userId: "support-system",
            userName: "Support Team",
            profileImg: ["https://miro.medium.com/v2/resize:fit:1400/1*v2Zf5KjSxZZwCzoNhLaNzg.jpeg"],
            userType: "admin"
        };

        dispatch(setSupportActiveUser(supportUser));
        setChatStarted(true);
        setShowChatButton(false);

        // Automatically send "connected" message
        const connectedMessage = {
            senderId: user._id,
            senderType: "student",
            message: "connected",
            type: 'TEXT',
        };

        if (socket) {
            console.log("📡 Emitting connected message to support");
            socket.emit('createSupportMessage', connectedMessage);
        }

        dispatch(createSupportMessage({
            message: "connected",
            type: 'TEXT'
        }));

        const optimisticMessage = {
            _id: `temp-${Date.now()}`,
            senderId: {
                _id: user._id,
                name: user.name,
                profileImg: user.profileImg || [""]
            },
            senderType: "student",
            recipientType: "admin",
            recipientId: {
                _id: "support-system",
                name: "Support Team",
            },
            message: "connected",
            type: 'TEXT',
            createdAt: new Date().toISOString(),
        };

        dispatch(addSupportClientMessage(optimisticMessage));
    };

    // Stop typing indicator and send stop event
    const stopTypingIndicator = () => {
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
        }

        if (socket) {
            const recipientId = assignedAdminId || "support-system";
            socket.emit("support-typing", {
                senderId: user._id,
                recipientId: recipientId,
                isTyping: false,
            });
            console.log('🟢 SENT TYPING STOP to:', recipientId);
        }
        setTyping(false);
    };

    // WebSocket listeners
    useEffect(() => {
        if (!socket) return;

        const handleNewSupportMessage = (data) => {
            console.log('🟢 SUPPORT message received from admin:', data);

            if (data.recipientId === user._id || data.senderId._id !== user._id) {
                dispatch(addSupportClientMessage(data));

                if (!isChatOpen) {
                    setHasNewMessages(true);
                }

                if (data.senderType === "admin" && !assignedAdminId) {
                    setAssignedAdminId(data.senderId._id);

                    const adminUser = {
                        userId: data.senderId._id,
                        userName: data.senderId.name || "Support Agent",
                        profileImg: data.senderId.profileImg || ["https://miro.medium.com/v2/resize:fit:1400/1*v2Zf5KjSxZZwCzoNhLaNzg.jpeg"],
                        userType: "admin"
                    };
                    dispatch(setSupportActiveUser(adminUser));
                }

                socket.emit("support-message-read", {
                    id: user._id,
                    recipientId: data.senderId?._id
                });
            }
        };

        const handleSupportChatTaken = (data) => {
            console.log('🟢 Chat assigned to admin:', data);
            if (data.studentId === user._id) {
                setAssignedAdminId(data.adminId);
                lastRecipientRef.current = data.adminId;

                const adminUser = {
                    userId: data.adminId,
                    userName: data.adminName || "Support Agent",
                    profileImg: ["https://miro.medium.com/v2/resize:fit:1400/1*v2Zf5KjSxZZwCzoNhLaNzg.jpeg"],
                    userType: "admin"
                };
                dispatch(setSupportActiveUser(adminUser));
            }
        };

        const handleSupportTyping = ({ senderId, isTyping }) => {
            console.log('🟢 STUDENT: Received typing event from admin:', { senderId, isTyping });
            // Only show typing if it's from the assigned admin
            if (senderId === assignedAdminId) {
                setTyping(isTyping);

                // Auto-clear typing after 3 seconds
                if (isTyping) {
                    if (typingTimeoutRef.current) {
                        clearTimeout(typingTimeoutRef.current);
                    }
                    typingTimeoutRef.current = setTimeout(() => {
                        setTyping(false);
                    }, 3000);
                }
            }
        };

        const handleSupportMessageAcknowledged = (data) => {
            console.log('🟢 Message acknowledged:', data);
        };

        // Register event listeners
        socket.on('newSupportMessage', handleNewSupportMessage);
        socket.on('supportChatTaken', handleSupportChatTaken);
        socket.on('support-typing', handleSupportTyping);
        socket.on('supportMessageAcknowledged', handleSupportMessageAcknowledged);

        // Cleanup function
        return () => {
            socket.off('newSupportMessage', handleNewSupportMessage);
            socket.off('supportChatTaken', handleSupportChatTaken);
            socket.off('support-typing', handleSupportTyping);
            socket.off('supportMessageAcknowledged', handleSupportMessageAcknowledged);
            stopTypingIndicator();
        };
    }, [socket, user._id, assignedAdminId, dispatch, isChatOpen]);

    // Update last recipient when assignedAdminId changes
    useEffect(() => {
        if (assignedAdminId) {
            lastRecipientRef.current = assignedAdminId;
        }
    }, [assignedAdminId]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopTypingIndicator();
        };
    }, []);

    const handleTyping = () => {
        if (!socket) return;

        // Clear existing timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        // Determine recipient - use assigned admin or support-system
        const recipientId = assignedAdminId || "support-system";
        lastRecipientRef.current = recipientId;

        // Send typing start event only if there's content
        if (content.trim()) {
            socket.emit("support-typing", {
                senderId: user._id,
                recipientId: recipientId,
                isTyping: true,
            });
            console.log('🟢 SENT TYPING START to:', recipientId);
        }

        // Set timeout to send typing stop
        typingTimeoutRef.current = setTimeout(() => {
            if (socket) {
                socket.emit("support-typing", {
                    senderId: user._id,
                    recipientId: recipientId,
                    isTyping: false,
                });
                console.log('🟢 SENT TYPING STOP to:', recipientId);
            }
            setTyping(false);
        }, 1500);
    };

    const handleInputChange = (e) => {
        setContent(e.target.value);
        handleTyping();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && content.trim()) {
            sendSupportMessageToServer();
        }
    };

    useEffect(() => {
        if (activeUser && chatStarted) {
            dispatch(getAllSupportChats("support"))
            console.log("🔄 Fetching support chats");
            setTyping(false);
            scrollToTop();
        }
    }, [activeUser, chatStarted])

    useEffect(() => {
        scrollToTop();
    }, [supportHistory.messages, activeUser])

    const scrollToTop = () => {
        if (flatListRef.current) {
            flatListRef.current.scrollTop = flatListRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        if (activeUser && socket) {
            socket.emit("support-message-read", {
                id: user._id,
                recipientId: assignedAdminId || "support-system"
            });

            dispatch(updateSupportReadStatus({
                userId: activeUser.userId,
                read: true,
            }));
        }
    }, [socket, activeUser, dispatch, assignedAdminId]);

    const sendSupportMessageToServer = () => {
        if (!content.trim() || !activeUser) {
            return;
        }

        const newSupportMessage = {
            senderId: user._id,
            senderType: "student",
            message: content,
            type: 'TEXT',
        };

        if (socket) {
            console.log("📡 Emitting createSupportMessage to:", assignedAdminId || "support-system");
            socket.emit('createSupportMessage', newSupportMessage);
        }

        dispatch(createSupportMessage({
            message: content,
            type: 'TEXT'
        }));

        const optimisticMessage = {
            _id: `temp-${Date.now()}`,
            senderId: {
                _id: user._id,
                name: user.name,
                profileImg: user.profileImg || [""]
            },
            senderType: "student",
            recipientType: "admin",
            recipientId: {
                _id: assignedAdminId || "support-system",
                name: "Support Team",
            },
            message: content,
            type: 'TEXT',
            createdAt: new Date().toISOString(),
        };

        dispatch(addSupportClientMessage(optimisticMessage));
        setContent('');

        // Stop typing indicator after sending message
        stopTypingIndicator();
    };

    const isStudentMessage = (msg) => {
        return msg.senderId._id === user._id || msg.senderType === "student";
    };

    return (
        <div className={messageStyle.mainContainerStyle}>
            {/* CHAT ICON - Always visible */}
            {!isChatOpen && (
                <div className={messageStyle.chatIconContainer} onClick={toggleChat}>
                    <div className={messageStyle.chatIcon}>
                        💬
                    </div>
                    {hasNewMessages && (
                        <div className={messageStyle.notificationBadge}></div>
                    )}
                </div>
            )}

            {/* CHAT BOX - Only visible when opened */}
            {isChatOpen && (
                <div className={messageStyle.chatBox}>
                    {/* Chat Header */}
                    <div className={messageStyle.chatHeader}>
                        <div className={messageStyle.headerContent}>
                            <div className={messageStyle.userInfoSection}>
                                <div className={messageStyle.avatarContainer}>
                                    <div className={messageStyle.onlineIndicator}>
                                        {(onlineUserIds.includes(assignedAdminId) || onlineUserIds.includes(activeUser?.userId)) && <OnlineIcon />}
                                    </div>
                                    <img
                                        src={
                                            activeUser && activeUser.profileImg && activeUser.profileImg.length > 0
                                                ? activeUser.profileImg[0]
                                                : "https://miro.medium.com/v2/resize:fit:1400/1*v2Zf5KjSxZZwCzoNhLaNzg.jpeg"
                                        }
                                        alt="Profile"
                                        className={messageStyle.profileImage}
                                    />
                                </div>
                                <div className={messageStyle.userInfo}>
                                    <span className={messageStyle.userName}>
                                        {"Support Team"}
                                        {assignedAdminId && (
                                            <span className={messageStyle.connectedBadge}>• Connected</span>
                                        )}
                                    </span>
                                    <span className={messageStyle.userStatus}>
                                        {typing ? (
                                            <span style={{ color: '#007bff', fontStyle: 'italic' }}>Typing...</span>
                                        ) : onlineUserIds.includes(assignedAdminId) || onlineUserIds.includes(activeUser?.userId)
                                            ? "Active Now"
                                            : "Connecting to support..."}
                                    </span>
                                </div>
                            </div>
                            <button className={messageStyle.closeButton} onClick={closeChat}>
                                <CrossIcon />
                            </button>
                        </div>
                    </div>

                    <div className={messageStyle.onlineStrip}>
                        <div className={messageStyle.onlineDot}></div>
                        <span>We are Online!</span>
                    </div>

                    {/* Chat Content Area */}
                    <div className={messageStyle.chatContent}>
                        {!chatStarted ? (
                            // Welcome State with Chat Button
                            <div className={messageStyle.welcomeState}>
                                <div className={messageStyle.welcomeContainer}>
                                    <img
                                        src="https://kingster-dev-public.s3.ap-south-1.amazonaws.com/video/60317320260138291570.png"
                                        alt="Support Chat"
                                        className={messageStyle.welcomeImage}
                                    />
                                    <h4 className={messageStyle.welcomeTitle}>
                                        Support Chat - Help Desk 💬
                                    </h4>
                                    <p className={messageStyle.welcomeText}>
                                        Need help? Our support team is here to assist you with any questions or issues.
                                    </p>

                                    {/* Chat with Support Button - Only show when chat hasn't started */}
                                    {showChatButton && (
                                        <div
                                            className={messageStyle.contactSupportButton}
                                            onClick={startSupportChat}
                                        >
                                            <PlusIcon />
                                            <span>Connect With Support</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Active Chat State
                            <div className={messageStyle.activeChat}>
                                {/* Messages Area */}
                                <div ref={flatListRef} className={messageStyle.messagesArea}>
                                    {supportHistory?.messages.length === 0 ? (
                                        <div className={messageStyle.noMessages}>
                                            <p>No messages yet. Start the conversation!</p>
                                            <p className={messageStyle.welcomeMessage}>
                                                Welcome to support! Our team is here to help you with any questions or issues you may have.
                                            </p>
                                        </div>
                                    ) : (
                                        supportHistory?.messages.map((msg, index) => {
                                            const isStudentMsg = isStudentMessage(msg);
                                            const currentMessageDate = new Date(msg.createdAt);
                                            const previousMessageDate = index > 0
                                                ? new Date(supportHistory.messages[index - 1].createdAt)
                                                : null;

                                            const formatDateLabel = (date) => {
                                                const today = new Date();
                                                const yesterday = new Date(today);
                                                yesterday.setDate(yesterday.getDate() - 1);

                                                if (date.toDateString() === today.toDateString()) {
                                                    return "Today";
                                                } else if (date.toDateString() === yesterday.toDateString()) {
                                                    return "Yesterday";
                                                } else {
                                                    return date.toLocaleDateString("en-GB", {
                                                        day: "2-digit",
                                                        month: "long",
                                                        year: "numeric",
                                                    });
                                                }
                                            };

                                            const isNewDay = !previousMessageDate ||
                                                currentMessageDate.toDateString() !== previousMessageDate.toDateString();

                                            return (
                                                <div key={msg._id || index}>
                                                    {isNewDay && (
                                                        <div className={messageStyle.dateDivider}>
                                                            <span>{formatDateLabel(currentMessageDate)}</span>
                                                        </div>
                                                    )}

                                                    {isStudentMsg ? (
                                                        // Student message (right side)
                                                        <div className={messageStyle.messageRow}>
                                                            <div className={messageStyle.messageBubbleRight}>
                                                                <div className={messageStyle.messageContent}>
                                                                    <p>{msg.message}</p>
                                                                    <div className={messageStyle.messageTime}>
                                                                        {formatDateToHHMM(msg.createdAt)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        // Admin message (left side)
                                                        <div className={messageStyle.messageRow}>
                                                            <div className={messageStyle.messageBubbleLeft}>
                                                                <div className={messageStyle.messageAvatar}>
                                                                    <img
                                                                        src={
                                                                            msg.senderId.profileImg?.[0] ||
                                                                            'https://miro.medium.com/v2/resize:fit:1400/1*v2Zf5KjSxZZwCzoNhLaNzg.jpeg'
                                                                        }
                                                                        alt="Support Agent"
                                                                        className={messageStyle.avatarImage}
                                                                    />
                                                                </div>
                                                                <div className={messageStyle.messageContent}>
                                                                    <p>{msg.message}</p>
                                                                    <div className={messageStyle.messageTime}>
                                                                        {formatDateToHHMM(msg.createdAt)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}

                                    {/* Typing Indicator */}
                                    {typing && (
                                        <div className={messageStyle.typingIndicator}>
                                            <div className={messageStyle.typingDots}>
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                            <span>{activeUser?.userName || 'Support'} is typing...</span>
                                        </div>
                                    )}
                                </div>

                                {/* Input Area - Only visible when chat has started and button is clicked */}
                                {chatStarted && !showChatButton && (
                                    <div className={messageStyle.inputArea}>
                                        <div className={messageStyle.inputContainer}>
                                            <div className={messageStyle.inputWrapper}>
                                                <TypeMessageIcon />
                                                <input
                                                    onChange={handleInputChange}
                                                    value={content}
                                                    type="text"
                                                    placeholder="Type your message to support..."
                                                    onKeyPress={handleKeyPress}
                                                    className={messageStyle.messageInput}
                                                />
                                            </div>
                                            <button
                                                onClick={() => content.trim() && sendSupportMessageToServer()}
                                                className={messageStyle.sendButton}
                                                disabled={!content.trim()}
                                            >
                                                <SentIcon />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
});

export default StudentSupportMessage;